import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { logEvent } from '@/helpers/eventLogger';
import { useNotifications } from '@/hooks/useNotifications';
import { safeLocalStorage } from '@/services/safeStorage';
import { getPaymentProvider, PaymentProvider } from '@tailored-pdf/api';
import axios, { CancelToken, isCancel } from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PaymentModal as DodoModal } from './Dodo';
import { PaymentModal as PaddleModal } from './Paddle';
import { PaymentModal as StripeModal } from './Stripe';

const STORAGE_PROVIDER_KEY = 'debug_provider';

function PaymentModalWrapper() {
  const {
    state: { needPayment, authToken },
  } = useContext(Context);

  const { notifyError } = useNotifications();

  const [provider, setProvider] = useState<PaymentProvider>();

  const initProvider = useCallback(
    async (ct: CancelToken) => {
      try {
        const resp = await getPaymentProvider({}, ct);
        // Denied storage must not look like a failed provider lookup: the catch below
        // would surface it to the user as a payment error.
        const debugProvider = safeLocalStorage.getItem(STORAGE_PROVIDER_KEY);
        setProvider((debugProvider ?? resp.data) as PaymentProvider);
      } catch (e) {
        !isCancel(e) && notifyError(getResponseError(e));
      }
    },
    [notifyError],
  );

  useEffect(() => {
    const source = axios.CancelToken.source();
    void initProvider(source.token);

    return () => source.cancel();
  }, [initProvider, authToken]);

  useEffect(() => {
    needPayment && logEvent({ logType: 'EVENT', eventType: 'SUBSCRIPTION_MODAL_OPENED' });
  }, [needPayment]);

  const content = useMemo(() => {
    switch (provider) {
      case PaymentProvider.STRIPE:
        return <StripeModal />;
      case PaymentProvider.DODO_PAYMENTS:
        return <DodoModal />;
      case PaymentProvider.PADDLE:
        return <PaddleModal />;
      default:
        return null;
    }
  }, [provider]);

  return needPayment && content;
}

export { PaymentModalWrapper as PaymentModal };
